import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Command, Cpu, Lock, Menu as MenuIcon, Pencil, SquarePen, Unplug, Users, WifiOff } from "lucide-react";
import { connectionLabel, profileTitle, turnRows, ProcessInfo, type TurnRow, type UsageStats } from "@timibot/core";
import { useApp, useWide } from "../../context";
import type { ChatStore } from "../../state/chat";
import { useObserve } from "../../state/observable";
import { Dialog, GlassButton, Menu, StatusDot, useMenu, type MenuEntry } from "../../ui/components";
import { TimiMascot, type Mood } from "../../ui/Mascot";
import { Composer } from "./Composer";
import { InteractionCard } from "./InteractionCards";
import { MessageRow, ProcessGroupView, WaitingRow, mediaOf } from "./Messages";
import { ModelPickerSheet } from "./ModelPicker";
import "./chat.css";

/**
 * 对话页外面一层：顶栏（抽屉按钮、标题菜单、新对话）。对话内容按 chat.id 重建（换对话时滚动位置、展开状态都要重来）。
 */
export function ChatHost() {
  const app = useApp();
  const chat = app.activeChat;
  useObserve(chat);
  const wide = useWide();
  const [renaming, setRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [showModels, setShowModels] = useState(false);
  const menu = useMenu();

  const model = chat.info.model ?? app.profiles.profile(app.selectedProfile)?.model;
  const subtitle = app.connection.kind === "connected" ? (model ?? "已连接") : connectionLabel(app.connection);

  const items: MenuEntry[] = [
    { label: "切换模型", detail: model ?? "默认模型", icon: <Cpu size={16} />, onSelect: () => setShowModels(true), disabled: app.isGuest && false },
    { label: "重命名对话", icon: <Pencil size={16} />, disabled: !chat.liveSessionID, onSelect: () => { setNewTitle(chat.displayTitle); setRenaming(true); } },
  ];
  if (app.profiles.profiles.length > 1) {
    items.push({ section: "切换机器人" });
    for (const p of app.profiles.profiles) {
      const api = app.profiles.apiName(p);
      items.push({ label: profileTitle(p), checked: api === app.selectedProfile, onSelect: () => app.selectProfile(api) });
    }
    if (!app.isGuest) items.push({ label: "管理机器人", icon: <Users size={16} />, onSelect: () => app.present("bots") });
  }
  items.push("divider", { label: "更多命令", detail: "在输入框里打 / 也能打开", icon: <Command size={16} />, onSelect: () => chat.setDraft("/") });

  return (
    <div className="chat-host">
      <header className="chat-header">
        {!wide ? <GlassButton icon={<MenuIcon size={20} />} label="对话列表" onClick={() => app.setSidebarOpen(true)} /> : <span className="header-slot" />}
        <button className="title-menu" onClick={(e) => menu.show(e.currentTarget)} aria-haspopup="menu">
          <span className="title-line">
            <span className="t-headline nowrap">{app.botTitle}</span>
            <ChevronDown size={13} strokeWidth={3} className="t-2" />
          </span>
          <span className="title-sub t-caption2 t-2">
            <StatusDot state={app.connection} />
            <span className="nowrap">{subtitle}</span>
          </span>
        </button>
        <GlassButton icon={<SquarePen size={19} />} label="新对话" onClick={() => app.newChat()} />
      </header>
      <ChatScreen key={chat.id} chat={chat} />
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} items={items} width={270} />
      <Dialog
        open={renaming}
        title="重命名对话"
        onClose={() => setRenaming(false)}
        input={{ value: newTitle, onChange: setNewTitle, placeholder: "标题" }}
        buttons={[{ title: "取消", role: "cancel" }, { title: "保存", onClick: () => void app.renameActive(newTitle) }]}
      />
      <ModelPickerSheet open={showModels} onClose={() => setShowModels(false)} chat={chat} />
    </div>
  );
}

/** 只渲染最后这么多行，往上翻到顶再点“更早的消息”加载更多 */
const ROW_PAGE = 40;

function ChatScreen({ chat }: { chat: ChatStore }) {
  const app = useApp();
  useObserve(chat);
  const scroller = useRef<HTMLDivElement>(null);
  const [rowLimit, setRowLimit] = useState(ROW_PAGE);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /** 跟随最新内容：用户自己往上翻才取消 */
  const follow = useRef(true);
  const [distance, setDistance] = useState(0);

  useEffect(() => { void chat.loadIfNeeded(); }, [chat]);

  const items = chat.transcript.items;
  const rows = useMemo(() => turnRows(items, chat.isRunning), [items, chat.isRunning]);
  const hidden = Math.max(0, rows.length - rowLimit);
  const shown = rows.slice(hidden);
  const mediaKey = useMemo(() => assistantMediaSources(shown).join("\n"), [shown]);
  const shownMedia = useMemo(() => new Set(mediaKey ? mediaKey.split("\n") : []), [mediaKey]);
  const latestReply = latestReplyID(shown);
  const usage = chat.transcript.usage ?? chat.info.usage;
  const last = shown[shown.length - 1];
  const awaiting = chat.isRunning && last?.type === "item" && ["user", "notice", "sideResult"].includes(last.item.kind.type);

  const scrollToBottom = (smooth = false) => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  // 内容变长（流式输出、图片加载完成）时继续贴着底部
  useLayoutEffect(() => {
    if (follow.current) scrollToBottom();
  });

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(() => { if (follow.current) scrollToBottom(); });
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [items.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // 自己发消息后始终滚到底
  const lastKind = items[items.length - 1]?.kind.type;
  useEffect(() => {
    if (lastKind === "user") { follow.current = true; scrollToBottom(); }
  }, [items.length, lastKind]);

  // 交互卡片占掉底部空间后，把最新内容顶上来
  useEffect(() => {
    if (chat.pendingRequests.length) { follow.current = true; setTimeout(() => scrollToBottom(), 80); }
  }, [chat.pendingRequests.length]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const d = el.scrollHeight - el.scrollTop - el.clientHeight;
    setDistance(d);
    follow.current = d < 60;
  };

  const toggle = (id: string) => setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const showJump = !follow.current && distance > 240;

  return (
    <div className="chat-screen">
      <ConnectionBanner />
      <div className="chat-body">
        {items.length === 0 ? (
          chat.storedSessionID && !chat.liveSessionID ? <RestoringView chat={chat} /> : <EmptyChatView chat={chat} />
        ) : (
          <div className="chat-scroll scroll" ref={scroller} onScroll={onScroll}>
            <div className="chat-column">
              {chat.isLoading && <div className="chat-loading"><span className="spinner" /></div>}
              {hidden > 0 && (
                <button className="older-button t-footnote medium t-2" onClick={() => { follow.current = false; setRowLimit((n) => n + ROW_PAGE); }}>
                  <ArrowUp size={13} /> 更早的消息
                </button>
              )}
              {shown.map((row) => <Row key={row.id} row={row} expanded={expanded.has(row.id)} onToggle={() => toggle(row.id)}
                shownMedia={shownMedia} usage={row.type === "item" && row.item.id === latestReply ? usage : undefined} />)}
              {awaiting && <WaitingRow status={chat.transcript.statusLine} />}
            </div>
          </div>
        )}
        {showJump && (
          <button className="jump-button" aria-label="回到最新消息" onClick={() => { follow.current = true; scrollToBottom(true); setDistance(0); }}>
            <ArrowDown size={17} strokeWidth={2.5} />
          </button>
        )}
      </div>
      {chat.pendingRequests.length > 0 && (
        <div className="interaction-stack">
          {chat.pendingRequests.map((r) => <InteractionCard key={r.id} request={r} chat={chat} />)}
        </div>
      )}
      <Composer chat={chat} />
      {app.route === "main" && <div className="tabbar-spacer" />}
    </div>
  );
}

function Row({ row, expanded, onToggle, shownMedia, usage }: {
  row: TurnRow; expanded: boolean; onToggle: () => void; shownMedia: Set<string>; usage?: UsageStats;
}) {
  if (row.type === "item") return <MessageRow item={row.item} mediaShownElsewhere={shownMedia} usage={usage} />;
  // 只有一段思考：沿用思考胶囊（本身就是折叠的）
  if (ProcessInfo.isReasoningOnly(row.group)) return <MessageRow item={row.group.items[0]} mediaShownElsewhere={shownMedia} />;
  return <ProcessGroupView group={row.group} expanded={expanded} onToggle={onToggle} mediaShownElsewhere={shownMedia} />;
}

/** 最新一条助手回复（它的操作栏右侧显示上下文用量） */
function latestReplyID(rows: TurnRow[]): string | undefined {
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i];
    if (r.type === "item" && r.item.kind.type === "assistant") return r.item.id;
  }
  return undefined;
}

/** 助手回复里出现过的媒体来源；生成图片的工具卡片不再重复显示同一张 */
function assistantMediaSources(rows: TurnRow[]): string[] {
  const out = new Set<string>();
  for (const r of rows) {
    const items = r.type === "item" ? [r.item] : r.group.items;
    for (const it of items) {
      if (it.kind.type === "assistant" && /MEDIA:|!\[|http/.test(it.kind.text)) for (const m of mediaOf(it.kind.text)) out.add(m.source);
    }
  }
  return [...out].sort();
}

/** 断线提示条 */
function ConnectionBanner() {
  const app = useApp();
  const c = app.connection;
  let icon, text, action: [string, () => void] | undefined;
  if (c.kind === "reconnecting") { icon = <WifiOff size={14} />; text = `连接中断，正在重连… ${c.reason}`; action = ["立即重试", () => app.retryNow()]; }
  else if (c.kind === "needsLogin") { icon = <Lock size={14} />; text = c.message; action = ["重新登录", () => app.presentConnect("reauth")]; }
  else if (c.kind === "idle" && app.route === "main") { icon = <Unplug size={14} />; text = "未连接"; action = ["连接", () => app.retryNow()]; }
  else return null;
  return (
    <div className="connection-banner t-caption">
      {icon}
      <span className="clamp2" style={{ flex: 1 }}>{text}</span>
      {action && <button className="semibold" onClick={action[1]}>{action[0]}</button>}
    </div>
  );
}

/** 新对话的空白页：只有 Timi 和一句问候 */
function EmptyChatView({ chat }: { chat: ChatStore }) {
  const app = useApp();
  const mood: Mood = app.connection.kind !== "connected" ? "sleeping" : chat.isSending || chat.isRunning ? "thinking" : chat.draft ? "listening" : "idle";
  const h = new Date().getHours();
  const greeting = h >= 5 && h < 11 ? "早上好" : h >= 11 && h < 14 ? "中午好" : h >= 14 && h < 18 ? "下午好" : "晚上好";
  const c = app.connection;
  const subtitle = c.kind === "connected" ? (app.profiles.profile(chat.profile)?.description || "想做什么，直接告诉我")
    : c.kind === "connecting" || c.kind === "reconnecting" ? "正在连接…" : "还没连上服务器";
  return (
    <div className="chat-empty">
      <TimiMascot mood={mood} size={96} />
      <div className="t-title3">{greeting}</div>
      <div className="t-subheadline t-2 chat-empty-sub">{subtitle}</div>
    </div>
  );
}

/** 打开上次的对话、但本机还没有缓存时：明确告诉用户“正在打开上次的对话” */
function RestoringView({ chat }: { chat: ChatStore }) {
  const app = useApp();
  const connected = app.connection.kind === "connected";
  return (
    <div className="chat-empty">
      <TimiMascot mood={connected ? "thinking" : "sleeping"} size={72} />
      <div className="t-subheadline t-2">{connected ? "正在打开上次的对话…" : "连上后继续上次的对话"}</div>
      {chat.title && <div className="t-headline nowrap" style={{ maxWidth: "80%" }}>{chat.title}</div>}
      <button className="restore-new t-subheadline medium" onClick={() => app.newChat()}><SquarePen size={15} /> 开始新对话</button>
    </div>
  );
}
